import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { BottomTabBar } from './BottomTabBar';
import { KeyboardDismiss } from './KeyboardDismiss';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/store/appStore';
import { useBgStreamStore } from '@/store/bgStreamStore';
import { useCompletionHaptics } from '@/features/tasks/lib/feedback';
import { SecretaryRecorderSheet } from '@/features/secretary/SecretaryRecorderSheet';

export function ShellLayout() {
  const { user } = useAuth();
  const isOnboardingComplete = useAppStore((s) => s.isOnboardingComplete);
  const completeOnboarding = useAppStore((s) => s.completeOnboarding);
  const connectBgStream = useBgStreamStore((s) => s.connect);
  const disconnectBgStream = useBgStreamStore((s) => s.disconnect);

  // R1 of doc 06: native haptic on task completion. Listens to the
  // `task-completed` window event dispatched by @unia/task-ui's
  // fireTaskCompleted().
  useCompletionHaptics();

  useEffect(() => {
    if (user && !isOnboardingComplete(user.id)) {
      completeOnboarding(user.id);
    }
  }, [user]);

  // Background-task SSE lives at the shell level so the connection survives
  // route changes (chat → notes → chat keeps the same EventSource). The store
  // also persists the rendered message timeline to localStorage so a full
  // reload still recovers in-flight pipeline progress.
  useEffect(() => {
    if (user?.id) {
      connectBgStream(user.id);
    }
    return () => {
      // Only fully tear down on auth change / unmount of the whole shell.
      // Route changes within the shell don't unmount this effect.
      disconnectBgStream();
    };
  }, [user?.id, connectBgStream, disconnectBgStream]);

  return (
    <div className="h-screen w-screen bg-background text-foreground font-sans overflow-hidden flex flex-col">
      {/* env(safe-area-inset-top) keeps page content below the iPhone
          notch / Dynamic Island. Non-notched devices and desktop have
          inset-top=0, so the padding is a no-op there. Bottom inset
          covers home-indicator + leaves room for the BottomTabBar.
          Without these the WebView extends edge-to-edge (we use
          viewport-fit=cover) and top-row UI (MomentumStrip, drawer
          handles, note toolbar) collides with the notch. */}
      {/* min-w-0 — та же беда, что в настольной версии: без него широкая
          доска канбана растягивает главную область и шапка уезжает за экран. */}
      {/* 🪤 25.09.2026: при открытой клавиатуре меню приложения уезжает вниз,
          а место под него оставалось — над клавиатурой висела чёрная полоса
          в палец высотой, и заметка обрывалась выше клавиатуры. */}
      <main className="flex-1 min-h-0 min-w-0 flex flex-col pt-[env(safe-area-inset-top,0px)] pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))] [html.kb-open_&]:pb-0 md:pb-0 md:pt-0">
        <Outlet />
      </main>
      <BottomTabBar />
      <KeyboardDismiss />
      {/* Secretary recorder sheet lives at shell level so it survives route changes (PRD §12.1). */}
      <SecretaryRecorderSheet />
    </div>
  );
}
